import SkeletonTable from "@/components/SkeletonTable";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { getInitials } from "@/lib/utils";
import { getListPeriode, getListPesertaVerifikasi } from "@/services/getData";
import { Periode } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BarChart3,
  Check,
  CheckCircle2,
  ClipboardList,
  Clock,
  Filter,
  LayoutDashboard,
  Medal,
  Target,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

// --- Types ---
type ValidasiType = {
  jml_belum_valid_final: number;
  jml_sudah_valid_final: number;
  nama_lengkap: string;
  total_verifikasi: number;
  user_id: number;
};

// --- API Calls ---
const rekapVerifikasiUkt = async (id_sys_periode?: string[] | number[]) => {
  const response = await api.post("/verifikator/peserta/rekap", {
    id_sys_periode,
  });
  return response?.data?.data;
};

export default function DashboardVerifikatorPage() {
  const { user } = useUser();
  const isVerifikatorUktAll = user?.jenis_user?.includes("verifikator_ukt_all");

  // ==========================================
  // QUERIES UNTUK TUGAS SAYA (MY WORKSPACE)
  // ==========================================
  const { data: dataPesertaValid, isPending: isPendingDataPesertaValid } =
    useQuery({
      queryKey: ["listPesertaVerifikasi", "valid", isVerifikatorUktAll],
      queryFn: () =>
        getListPesertaVerifikasi(
          undefined,
          "99999", // Fetch all to get length
          undefined,
          "valid",
          isVerifikatorUktAll ? "all" : "",
        ),
      enabled: !!user,
    });

  const {
    data: dataPesertaBelumValid,
    isPending: isPendingDataPesertaBelumValid,
  } = useQuery({
    queryKey: ["listPesertaVerifikasi", "belum-valid", isVerifikatorUktAll],
    queryFn: () =>
      getListPesertaVerifikasi(
        undefined,
        "99999", // Fetch all to get length
        undefined,
        "belum-valid",
        isVerifikatorUktAll ? "all" : "",
      ),
    enabled: !!user,
  });

  const jumlahPesertaValid = dataPesertaValid?.data?.length || 0;
  const jumlahPesertaBelumValid = dataPesertaBelumValid?.data?.length || 0;
  const totalPesertaSaya = jumlahPesertaValid + jumlahPesertaBelumValid;
  const progressSaya =
    totalPesertaSaya === 0 ? 0 : (jumlahPesertaValid / totalPesertaSaya) * 100;

  // ==========================================
  // QUERIES UNTUK REKAPITULASI TIM (TEAM ANALYTICS)
  // (Hanya di-fetch jika user adalah Verifikator All)
  // ==========================================
  const [selectedPeriodes, setSelectedPeriodes] = useState<string[]>([]);

  const { data: dataRekap, isPending: isPendingRekap } = useQuery({
    queryKey: ["dataRekapVerifikasi", selectedPeriodes],
    queryFn: () => rekapVerifikasiUkt(selectedPeriodes),
    enabled: !!isVerifikatorUktAll, // Optimasi: jangan fetch jika bukan koordinator
  });

  const { data: listDataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
    enabled: !!isVerifikatorUktAll,
  });

  const filteredListPeriode = listDataPeriode?.filter((periode: Periode) => {
    return (
      periode?.jalur_jenis?.nama_jalur_jenis_slug !== "pascasarjana" &&
      periode?.jalur_jenis?.nama_jalur_jenis_slug !== "profesi"
    );
  });

  const summary = useMemo(() => {
    if (!dataRekap) return { total: 0, done: 0, pending: 0, progress: 0 };

    const total = dataRekap.reduce(
      (acc: number, curr: ValidasiType) => acc + Number(curr.total_verifikasi),
      0,
    );
    const done = dataRekap.reduce(
      (acc: number, curr: ValidasiType) =>
        acc + Number(curr.jml_sudah_valid_final),
      0,
    );
    const pending = dataRekap.reduce(
      (acc: number, curr: ValidasiType) =>
        acc + Number(curr.jml_belum_valid_final),
      0,
    );
    const progress = total === 0 ? 0 : (done / total) * 100;

    return { total, done, pending, progress };
  }, [dataRekap]);

  return (
    <div className="flex flex-col gap-10 w-full pb-12 p-2 sm:p-4">
      {/* --- HEADER DASHBOARD --- */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white flex items-center gap-3">
          <div className="p-3 bg-primary/10 text-primary rounded-2xl">
            <LayoutDashboard className="w-7 h-7" />
          </div>
          Dashboard Verifikator
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm sm:text-base ml-[3.5rem]">
          Selamat datang,{" "}
          <span className="font-semibold text-slate-700 dark:text-slate-300">
            {user?.nama_lengkap || "Verifikator"}
          </span>
          ! Pantau tugas dan capaian harian Anda di sini.
        </p>
      </div>

      {/* =========================================
          SECTION 1: QUICK ACTIONS (MY WORKSPACE)
      ========================================= */}
      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary" /> Tugas Saya
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Card Belum Divalidasi */}
          <Link
            to={"/verifikator/verifikasi-berkas?statusValidasi=belum-valid"}
            className="group outline-none"
          >
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-orange-100 dark:border-orange-900/30 shadow-sm hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-700 transition-all duration-300 relative overflow-hidden focus-within:ring-2 focus-within:ring-orange-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-slate-950">
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-orange-100 to-orange-50 dark:from-orange-900/20 dark:to-orange-800/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

              <div className="relative z-10 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                      Perlu Diperiksa
                    </p>
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <h3 className="text-4xl font-black text-slate-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                      {isPendingDataPesertaBelumValid ? (
                        <span className="animate-pulse">...</span>
                      ) : (
                        jumlahPesertaBelumValid
                      )}
                    </h3>
                    <span className="text-slate-500 dark:text-slate-400 font-medium">
                      Peserta
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-2xl text-orange-500 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <Clock className="w-8 h-8" />
                </div>
              </div>

              <div className="relative z-10 mt-6 flex items-center text-sm font-medium text-orange-600 dark:text-orange-400 group-hover:text-orange-700 dark:group-hover:text-orange-300">
                Mulai Verifikasi{" "}
                <ArrowRight className="w-4 h-4 ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
              </div>
            </div>
          </Link>

          {/* Card Sudah Divalidasi */}
          <Link
            to={"/verifikator/verifikasi-berkas?statusValidasi=valid"}
            className="group outline-none"
          >
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-emerald-100 dark:border-emerald-900/30 shadow-sm hover:shadow-lg hover:border-emerald-300 dark:hover:border-emerald-700 transition-all duration-300 relative overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500 focus-within:ring-offset-2 dark:focus-within:ring-offset-slate-950">
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/20 dark:to-emerald-800/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />

              <div className="relative z-10 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                      Selesai Divalidasi
                    </p>
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <h3 className="text-4xl font-black text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                      {isPendingDataPesertaValid ? (
                        <span className="animate-pulse">...</span>
                      ) : (
                        jumlahPesertaValid
                      )}
                    </h3>
                    <span className="text-slate-500 dark:text-slate-400 font-medium">
                      Peserta
                    </span>
                  </div>
                </div>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl text-emerald-500 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
              </div>

              <div className="relative z-10 mt-6 flex items-center text-sm font-medium text-emerald-600 dark:text-emerald-400 group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
                Lihat Daftar{" "}
                <ArrowRight className="w-4 h-4 ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="w-full h-px bg-slate-200 dark:bg-slate-800" />

      {/* =========================================
          SECTION 2A: MY PERSONAL ANALYTICS (Tampil untuk semua)
      ========================================= */}
      {!isVerifikatorUktAll && (
        <section className="flex flex-col gap-6">
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" /> Capaian Verifikasi
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Visualisasi Circular Progress */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center items-center gap-4 text-center md:col-span-1">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50%"
                    cy="50%"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="transparent"
                    className="text-slate-100 dark:text-slate-800"
                  />
                  <circle
                    cx="50%"
                    cy="50%"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="transparent"
                    strokeDasharray={2 * Math.PI * 56}
                    strokeDashoffset={
                      2 * Math.PI * 56 -
                      (progressSaya / 100) * (2 * Math.PI * 56)
                    }
                    strokeLinecap="round"
                    className="text-primary transition-all duration-1000 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-black text-slate-900 dark:text-white">
                    {progressSaya.toFixed(0)}%
                  </span>
                </div>
              </div>
              <div>
                <p className="font-bold text-slate-800 dark:text-slate-200">
                  Progres Penyelesaian
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Dari total {totalPesertaSaya} penugasan
                </p>
              </div>
            </div>

            {/* Motivation Card */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 rounded-2xl p-6 border border-primary/20 dark:border-primary/20 shadow-sm flex flex-col justify-center md:col-span-2 relative overflow-hidden">
              <Medal className="absolute -bottom-4 -right-4 w-32 h-32 text-primary/10 dark:text-primary/20 -rotate-12" />
              <div className="relative z-10">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {progressSaya === 100
                    ? "Kerja Bagus! Luar Biasa! 🎉"
                    : progressSaya >= 50
                      ? "Sedikit lagi selesai! Terus semangat! 🔥"
                      : "Mari selesaikan target hari ini! 💪"}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {progressSaya === 100
                    ? "Anda telah menyelesaikan seluruh penugasan verifikasi yang diberikan. Terima kasih atas dedikasi dan ketelitian Anda."
                    : `Anda telah memverifikasi ${jumlahPesertaValid} dari ${totalPesertaSaya} berkas peserta. Ketelitian Anda sangat menentukan masa depan peserta. Pastikan selalu memeriksa kesesuaian data input dengan dokumen PDF yang diunggah.`}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* =========================================
          SECTION 2B: TEAM ANALYTICS (Hanya untuk Koordinator)
      ========================================= */}
      {isVerifikatorUktAll && (
        <section className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" /> Rekapitulasi Tim
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Ringkasan beban kerja dan progres seluruh verifikator.
            </p>
          </div>

          {/* SUMMARY CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full text-slate-600 dark:text-slate-300 shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                  Total Target
                </p>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">
                  {isPendingRekap ? "..." : summary.total}{" "}
                  <span className="text-xs font-normal text-slate-500 lowercase">
                    berkas
                  </span>
                </h3>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-emerald-600 dark:text-emerald-400 shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                  Total Selesai
                </p>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">
                  {isPendingRekap ? "..." : summary.done}{" "}
                  <span className="text-xs font-normal text-emerald-500">
                    ({summary.progress.toFixed(1)}%)
                  </span>
                </h3>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600 dark:text-amber-400 shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">
                  Sisa Pending
                </p>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">
                  {isPendingRekap ? "..." : summary.pending}{" "}
                  <span className="text-xs font-normal text-slate-500 lowercase">
                    berkas
                  </span>
                </h3>
              </div>
            </div>
          </div>

          {/* FILTER SECTION */}
          <div className="sticky top-14 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 transition-colors duration-200">
            <div className="px-3 flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400 border-b sm:border-b-0 sm:border-r border-slate-200 dark:border-slate-700 pb-2 sm:pb-0 shrink-0">
              <Filter className="w-4 h-4" /> Filter Jalur:
            </div>
            <div className="flex flex-nowrap overflow-x-auto gap-2 p-1 custom-scrollbar pb-2 sm:pb-1">
              {filteredListPeriode?.map((periode: Periode) => {
                const id = periode?.id_sys_periode || "";
                const isSelected = selectedPeriodes.includes(id);

                return (
                  <button
                    key={id}
                    onClick={() =>
                      setSelectedPeriodes((prev) =>
                        prev.includes(id)
                          ? prev.filter((p) => p !== id)
                          : [...prev, id],
                      )
                    }
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                      isSelected
                        ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    }`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                    {periode?.jalur_jenis?.nama_jalur_jenis}
                  </button>
                );
              })}
              {filteredListPeriode?.length === 0 && (
                <span className="text-sm text-slate-400 italic px-2 py-1">
                  Tidak ada data periode.
                </span>
              )}
            </div>
          </div>

          {/* MAIN TABLE SECTION */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
            {isPendingRekap ? (
              <SkeletonTable />
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 dark:bg-slate-900/80 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="px-6 py-4 font-semibold whitespace-nowrap w-[30%]">
                        Verifikator
                      </th>
                      <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">
                        Belum Divalidasi
                      </th>
                      <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">
                        Selesai
                      </th>
                      <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">
                        Total Beban
                      </th>
                      <th className="px-6 py-4 font-semibold whitespace-nowrap w-[30%]">
                        Progress Capaian
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                    {dataRekap && dataRekap.length > 0 ? (
                      dataRekap.map((item: ValidasiType) => {
                        const itemTotal = Number(item.total_verifikasi);
                        const itemDone = Number(item.jml_sudah_valid_final);
                        const percentage =
                          itemTotal === 0 ? 0 : (itemDone / itemTotal) * 100;

                        const isComplete = percentage === 100;
                        const progressColor = isComplete
                          ? "bg-emerald-500"
                          : "bg-primary";

                        return (
                          <tr
                            key={item?.user_id}
                            className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-700">
                                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                                    {getInitials(item.nama_lengkap || "V")}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">
                                  {item.nama_lengkap}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">
                                {item.jml_belum_valid_final}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium">
                                {item.jml_sudah_valid_final}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-bold">
                                {item.total_verifikasi}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3 w-full max-w-[200px]">
                                <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-1000 ease-out ${progressColor}`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-slate-500 min-w-[2.5rem] text-right">
                                  {percentage.toFixed(0)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-12 text-center text-slate-500 dark:text-slate-400"
                        >
                          <div className="flex flex-col items-center justify-center gap-2">
                            <BarChart3 className="w-8 h-8 opacity-20" />
                            <p>Belum ada data rekapitulasi verifikasi.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
